import { MapPin, Phone, PhoneOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { openSessionCallTab } from '@/features/video/lib/openSessionCallTab';

import { useRingtone } from '../hooks/useRingtone';
import { useIncomingCallStore } from '../stores/incomingCallStore';

const RINGTONE_SRC = '/sounds/incoming-call.mp3';

/**
 * Global ringing modal for psychologists / chiefs / volunteers. Mounted once
 * inside ClinicalShell — listens to `useIncomingCallStore` and renders only
 * when an incoming-call event has been received via SSE.
 *
 * Join → navigates to the existing fullscreen call route, which mints the
 *        LiveKit token and joins the room.
 * Dismiss → clears the store. There is no backend "decline" call in v1; if
 *           the psy ignores the ring the client's call screen times out on
 *           its own (LiveKit room never opens, ghost-row sweep handles it).
 */
export function IncomingCallDialog() {
  const incoming = useIncomingCallStore((s) => s.incoming);
  const clearIncoming = useIncomingCallStore((s) => s.clearIncoming);

  useRingtone({ active: !!incoming, src: RINGTONE_SRC });

  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!incoming) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(id);
    };
  }, [incoming]);

  const elapsed = incoming ? Math.max(0, Math.floor((now - incoming.receivedAt) / 1000)) : 0;

  if (!incoming) return null;

  const handleJoin = () => {
    const sessionId = incoming.sessionId;
    clearIncoming();
    // Open the call in a new tab so the practitioner's current workflow
    // (session list, drawer, in-progress notes) stays visible in the
    // original tab. Surface is 'clinical' since this dialog is only
    // mounted in ClinicalShell.
    openSessionCallTab(sessionId, 'clinical');
  };

  const handleDismiss = () => {
    clearIncoming();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) clearIncoming();
      }}
    >
      <DialogContent
        className="max-w-md gap-0 overflow-hidden p-0"
        aria-live="assertive"
        // The ringing dialog is informational + actionable; we don't want
        // accidental Esc / outside-click dismisses to silently swallow a
        // call. Keep `onOpenChange` (X button) but suppress the auto-close
        // pathways below.
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2 px-7 pt-7 pr-12">
          <div className="text-primary-600 inline-flex items-center gap-2">
            <span
              className="bg-primary-500 inline-block size-2 animate-pulse rounded-full"
              aria-hidden
            />
            <span className="text-meta-sm tracking-eyebrow font-medium uppercase">
              Incoming portal call
            </span>
          </div>
          <DialogTitle className="font-display text-accent-ink mt-1 text-[1.375rem] leading-tight font-medium">
            {incoming.clientName}
          </DialogTitle>
          {/* Location chip sits directly under the name because "who is
              calling from where" is a single piece of context the psy
              evaluates before picking up. Bold-weighted so it competes
              with the name, not the description. */}
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-neutral-700">
            <MapPin className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span className="text-body-sm font-medium">{incoming.locationName}</span>
          </div>
          <DialogDescription className="text-body-sm mt-1 leading-relaxed text-neutral-600">
            Wants to start a portal helpline call. Ringing for {elapsed}s.
          </DialogDescription>

          {incoming.concerns.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {incoming.concerns.map((concern) => (
                <span
                  key={concern}
                  className="bg-primary-50 text-primary-700 rounded-full px-2.5 py-1 text-xs font-medium"
                >
                  {concern}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-7 flex items-center justify-end gap-2 border-t border-(--color-border) bg-neutral-50/60 px-5 py-3.5">
          <Button variant="ghost" size="md" onClick={handleDismiss} className="text-error-700">
            <PhoneOff className="h-4 w-4" strokeWidth={1.75} />
            Dismiss
          </Button>
          <Button variant="primary" size="md" onClick={handleJoin} className="min-w-28">
            <Phone className="h-4 w-4" strokeWidth={1.75} />
            Join call
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
